import {
  fetchAuthSession,
  signIn,
  signOut,
  fetchUserAttributes,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
  confirmSignIn,
} from '@aws-amplify/auth';

import { default as envConfig } from '@/config/runtimeConfig';
import { AuthMessage, UserAuth } from '@/models/auth/UserAuth';
import { User } from '@/models/auth/User';
import { Pinia, Store } from '@/store/pinia-class-component';
import * as Sentry from '@sentry/browser';
import { WebEvents } from '@/native/plugins/WebEvents';
import { useOrderStore } from '@/store/order/orderStore';
import { Amplify } from 'aws-amplify';
import { AuthUserAttributes } from '@aws-amplify/auth/src/types';
import {
  GetCurrentUserOutput,
  UserAttributeKey,
} from '@aws-amplify/auth/src/providers/cognito/types';
import { DeviceFileSystem } from '@/native/plugins/DeviceFileSystem';
import { UserDeviceInfo } from '@/native/plugins/DeviceFileSystem/types';

const configureAmplify = () => {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: envConfig.cognitoUserPoolId,
        userPoolClientId: envConfig.cognitoClientID,
      },
    },
    Geo: {
      LocationService: {
        region: 'eu-central-1',
      },
    },
  });
};

export function useUserStore() {
  return new UserStore();
}

@Store
class UserStore extends Pinia {
  private _user:
    | AuthUserAttributes<UserAttributeKey>
    | GetCurrentUserOutput
    | undefined;
  private _userSettings: {
    showValveHint: boolean;
    showGatewayLocationHint: boolean;
  };
  private _userDeviceInfo: UserDeviceInfo;

  constructor() {
    super();
    configureAmplify();
    this._userDeviceInfo = {
      imei: '',
      name: '',
    };
    this._user = undefined;
    this._userSettings = {
      showValveHint: true,
      showGatewayLocationHint: true,
    };
  }

  public get getUsername() {
    if (!this._user) {
      return;
    }
    return this._user.username;
  }

  public get userDeviceInfo() {
    return this._userDeviceInfo;
  }

  public get userAttributes() {
    return this._user;
  }

  public get isValveHintVisible() {
    return this._userSettings.showValveHint;
  }

  public async isUserDeveloper() {
    const userGroups = await this.getUserGroups();
    const isDeveloper = userGroups.some((group) => group.includes('developer'));
    const isAdmin = userGroups.some((group) => group.includes('admin'));
    return isDeveloper || isAdmin;
  }

  public async isUserDispatcher() {
    const userGroups = await this.getUserGroups();
    return (
      userGroups.length > 0 &&
      userGroups.every((group) => group.includes('dispatcher'))
    );
  }

  private async getUserGroups() {
    const { tokens } = await fetchAuthSession();
    const groups = tokens?.idToken?.payload['cognito:groups'];
    if (!groups || !Array.isArray(groups)) {
      return [];
    }
    return groups as string[];
  }

  public get isGatewayLocationHintVisible() {
    return this._userSettings.showGatewayLocationHint;
  }

  public async getAccessToken() {
    const { tokens } = await fetchAuthSession();
    return tokens?.accessToken;
  }

  public async fetchCurrentUser() {
    const currentUser = await getCurrentUser();
    const userAttributes = await fetchUserAttributes();
    this._user = { ...currentUser, ...userAttributes };
  }

  public async setUser() {
    await this.fetchCurrentUser();
    if (this._user?.username) {
      Sentry.setUser({
        username: this._user.username,
        id: this._userDeviceInfo.name,
      });
      WebEvents.onUserAuthenticated({
        username: this._user.username,
        id: this._userDeviceInfo.name,
      });
    }
  }

  public async login(credentials: User) {
    return signIn({
      username: credentials.username,
      password: credentials.password,
    })
      .then((response) => {
        const userAuth = new UserAuth();
        userAuth.setAuthSuccess(true);

        if (response.nextStep.signInStep === 'DONE') {
          this.clearUserOrders();
          userAuth.setChallengeName(response.nextStep.signInStep);
          this.clearUserOrders();
          return userAuth;
        }

        userAuth.setChallengeName(response.nextStep.signInStep);
        return userAuth;
      })
      .catch((error) => {
        console.debug(error);
        return this.generateAuthentication(false, error.message);
      });
  }

  public async identifyUserDevice(): Promise<UserDeviceInfo | undefined> {
    const savedDeviceInfo = window.localStorage.getItem('deviceId');
    if (savedDeviceInfo) {
      this._userDeviceInfo = JSON.parse(savedDeviceInfo);
      return Promise.resolve(this._userDeviceInfo);
    }

    return DeviceFileSystem.readDeviceFileFromDocuments()
      .then((deviceInfo) => {
        if (deviceInfo && deviceInfo.name) {
          window.localStorage.setItem('deviceId', JSON.stringify(deviceInfo));
          this._userDeviceInfo = deviceInfo;
          return deviceInfo;
        }

        return undefined;
      })
      .catch((e) => {
        console.debug('Error Happened: ' + e);
        return undefined;
      });
  }

  public async forgetPassword(credentials: User) {
    return resetPassword({ username: credentials.username })
      .then(() => {
        return this.generateAuthentication(true, AuthMessage.EMAIL_SENT);
      })
      .catch((error) => {
        return this.generateAuthentication(false, error.message);
      });
  }

  public async setNewPassword(credentials: User) {
    return confirmSignIn({
      challengeResponse: credentials.newPassword,
    })
      .then(() => {
        return this.generateAuthentication(true, AuthMessage.PASSWORD_CHANGED);
      })
      .catch((error) => {
        return this.generateAuthentication(false, error.message);
      });
  }

  public async changePasswordWithResetCode(credentials: User) {
    return confirmResetPassword({
      username: credentials.username,
      confirmationCode: credentials.resetCode,
      newPassword: credentials.newPassword,
    })
      .then(() => {
        return this.generateAuthentication(true, AuthMessage.PASSWORD_CHANGED);
      })
      .catch((error) => {
        return this.generateAuthentication(false, error.message);
      });
  }

  public async logout() {
    this.clearUserOrders();
    try {
      await signOut();
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }

  private clearUserOrders() {
    useOrderStore().clearOrders();
  }

  generateAuthentication(success: boolean, message: AuthMessage) {
    return new UserAuth().setAuthSuccess(success).setMessage(message);
  }

  public hideValveHint() {
    this._userSettings.showValveHint = false;
  }

  public hideGatewayLocationHint() {
    this._userSettings.showGatewayLocationHint = false;
  }

  public allowGatewayLocationHint() {
    this._userSettings.showGatewayLocationHint = true;
  }
}
